import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { getOrgTokens, ORG_PALETTE } from '../theme/style/organizationPalette';

const LifecycleStageCards = ({
    title,
    subtitle,
    value,
    unit,
    icon: Icon,
    accent,
    bg,
    type, // 'chain', 'scope1', 'scope2', 'scope3', 'gwp', 'cost', 'shield'
    delay = 0,
    variant = 'default', // 'default' or 'stage'
    forceTons = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const typeTokens = (isDark && type) ? ORG_PALETTE.metricCards[type] : null;

    // Helper for value conversion + smart unit display
    const { displayValue, displayUnit } = useMemo(() => {
        if (typeof value !== 'number') return { displayValue: value, displayUnit: unit };

        // Handle circularity index
        if (unit?.includes('Index')) return { displayValue: value.toFixed(2), displayUnit: unit };

        // Smart kg ↔ tonne conversion for CO₂ units
        if (unit?.includes('CO')) {
            if (value >= 100 || forceTons) {
                // show in tonnes
                const valInTons = value / 1000;
                let decimals = 2;
                if (valInTons > 0 && valInTons < 0.1) {
                    decimals = 3;
                }
                return {
                    displayValue: valInTons.toLocaleString(undefined, {
                        minimumFractionDigits: valInTons === 0 ? 1 : 2,
                        maximumFractionDigits: decimals
                    }),
                    displayUnit: 't CO₂e'
                };
            } else {
                // show in kg
                return {
                    displayValue: value.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                    }),
                    displayUnit: 'kg CO₂e'
                };
            }
        }

        return { displayValue: Math.round(value).toLocaleString(), displayUnit: unit };
    }, [value, unit, forceTons]);

    const isStage = variant === 'stage';

    return (
        <Box
            className={`eng-metric-card eng-metric-card--${variant}`}
            sx={{
                animationDelay: `${delay}s`,
                background: bg || tok.bg.metricDefault,
                p: '20px 24px',
                borderRadius: tok.radius.card,
                border: '1px solid',
                borderColor: typeTokens?.borderDark || tok.border.card,
                minHeight: '100px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '20px',
                boxShadow: tok.shadow.card,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                    transform: ORG_PALETTE.animation.cardHover,
                    boxShadow: tok.shadow.cardHover
                }
            }}>

            {/* Left Side: Icon */}
            {Icon && (
                <Box className="eng-metric-card__icon-box" sx={{
                    background: typeTokens?.iconBgDark || (isDark ? 'rgba(255,255,255,0.08)' : '#fff'),
                    width: 44,
                    height: 44,
                    borderRadius: ORG_PALETTE.radius.iconSm || '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: tok.shadow.iconBox,
                    flexShrink: 0
                }}>
                    <Icon size={24} color={(isDark && typeTokens?.iconColorDark) ? typeTokens.iconColorDark : accent} />
                </Box>
            )}

            {/* Right Side: Value and Titles */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexGrow: 1,
                minWidth: 0,
                gap: '16px'
            }}>
                <Typography className="eng-metric-card__value" sx={{
                    fontSize: '24px !important',
                    fontWeight: '700 !important',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px',
                    lineHeight: 1.1,
                    color: isDark ? '#ffffff' : '#0f172a',
                    whiteSpace: 'nowrap',
                    textAlign: 'left'
                }}>
                    {displayValue}
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: '500 !important', color: isDark ? 'rgba(255,255,255,0.7)' : '#334155' }}>
                        {displayUnit || ''}
                    </Typography>
                </Typography>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    textAlign: 'right',
                    minWidth: 0,
                    width: '100%'
                }}>
                    <Typography className="eng-metric-card__title" sx={{
                        fontSize: '15.5px !important',
                        fontWeight: '600 !important',
                        color: isDark ? 'rgba(255,255,255,1)' : '#0f172a',
                        fontFamily: 'Poppins',
                        lineHeight: 1.2,
                        mb: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        textAlign: 'right',
                        width: '100%'
                    }}>
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography sx={{
                            fontSize: '12.5px',
                            fontWeight: '500 !important',
                            color: isDark ? 'rgba(255,255,255,0.7)' : '#334155',
                            textAlign: 'right',
                            width: '100%'
                        }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default LifecycleStageCards;
